import CloseIcon from '@mui/icons-material/Close';
import {Box, Divider, Typography, TypographyOwnProps} from '@mui/material';
import React, {forwardRef} from 'react';

interface SnackbarContentProps {
  icon: React.ReactElement;
  title: string;
  titleColor: TypographyOwnProps['color'];
  color: TypographyOwnProps['color'];
  content: React.ReactNode;
  dividerColor: boolean;
  bgWhite?: boolean;
  onClose?: () => void;
}

// `color: 'success'` n'est pas un chemin de palette dans `sx` : le titre restait de la couleur héritée.
// Le succès prend la teinte de texte des puces, `success.main` étant un vert pâle illisible sur blanc.
const TITLE_PALETTE_PATHS: Record<string, string | undefined> = {
  primary: 'primary.main',
  secondary: 'secondary.main',
  error: 'error.main',
  warning: 'warning.main',
  info: 'info.main',
  success: 'chip.successText',
};

export const SnackbarContent = forwardRef<HTMLDivElement, SnackbarContentProps>(
  ({icon, title, titleColor, color, content, dividerColor, bgWhite, onClose}, ref) => {
    return (
      <Box
        ref={ref}
        sx={{
          backgroundColor: bgWhite ? (theme) => theme.palette.background.paper : color,
          minWidth: '21.875rem',
          maxWidth: '600px',
          boxShadow: 'md',
          borderRadius: 'md',
          p: 1,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" color="secondary" p={1.5}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              lineHeight: 0,
              color: bgWhite || color === 'light' ? 'text' : 'white',
              gap: 1,
            }}
          >
            <Box>{icon}</Box>
            <Typography
              variant="button"
              sx={{
                fontWeight: 'medium',
                color: typeof titleColor === 'string' ? (TITLE_PALETTE_PATHS[titleColor] ?? titleColor) : titleColor,
              }}
            >
              {title}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" lineHeight={0}>
            {onClose && (
              <CloseIcon
                sx={{
                  fontWeight: ({typography: {fontWeightBold}}) => fontWeightBold,
                  cursor: 'pointer',
                  marginLeft: 2,
                  transform: 'translateY(-1px)',
                }}
                onClick={onClose}
              />
            )}
          </Box>
        </Box>
        <Divider sx={{margin: 0, opacity: dividerColor ? 0.6 : undefined}} />
        <Box p={1.5} color={bgWhite || color === 'light' ? 'text' : 'white'}>
          {content}
        </Box>
      </Box>
    );
  },
);
